import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, 
  PieChart, Pie, Cell, ResponsiveContainer, Legend 
} from 'recharts';
import { ChevronLeft, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface TaskData {
  completed: number;
  'in-progress': number;
  todo: number;
  total: number;
}

interface ProjectData {
  _id: string;
  title: string;
  progress?: number;
  deadline?: string;
}

interface ProgressResponse {
  project: ProjectData;
  tasks: TaskData;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const API_BASE_URL = getApiBaseUrl();

const COLORS = ['#10B981', '#F59E0B', '#EF4444'];

const ProjectProgress: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<ProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Authentication required. Please login.');
      }

      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/progress`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to load progress data');
      }

      const json = await response.json();
      
      // Ensure tasks data is properly formatted
      const tasks = {
        completed: json.tasks?.completed || 0,
        'in-progress': json.tasks?.['in-progress'] || 0,
        todo: json.tasks?.todo || 0,
        total: json.tasks?.total || 0
      };

      setData({
        project: json.project,
        tasks
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch progress data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgress();
  }, [projectId]);

  const calculateCompletionPercentage = () => {
    if (data?.project?.progress !== undefined) {
      return Math.max(0, Math.min(100, data.project.progress));
    }
    if (data?.tasks?.total && data.tasks.total > 0) {
      return Math.round((data.tasks.completed / data.tasks.total) * 100);
    }
    return 0;
  };

  const calculateDaysRemaining = () => {
    if (!data?.project?.deadline) return null;
    const deadline = new Date(data.project.deadline);
    const today = new Date();
    return Math.max(0, Math.floor((deadline.getTime() - today.getTime()) / (86400000)));
  };

  const getChartData = () => {
    if (!data?.tasks || data.tasks.total === 0) return [];
    
    return [
      { name: 'Completed', value: data.tasks.completed },
      { name: 'In Progress', value: data.tasks['in-progress'] },
      { name: 'To Do', value: data.tasks.todo }
    ];
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'text-green-600';
    if (progress >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const completionPercentage = calculateCompletionPercentage();
  const daysRemaining = calculateDaysRemaining();
  const chartData = getChartData();
  const hasTasks = data?.tasks && data.tasks.total > 0;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="animate-spin w-6 h-6 text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <Link 
            to={`/dashboard/projects/${projectId}`} 
            className="inline-flex items-center gap-2 text-blue-600 hover:underline"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Project
          </Link>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 mb-2">
            {error.includes('permission') || error.includes('own') 
              ? 'Access Denied' 
              : 'Failed to Load'}
          </h3>
          <p className="text-red-600 mb-6">{error}</p>
          
          <div className="flex justify-center gap-4">
            {error.includes('login') || error.includes('Authentication') ? (
              <Link
                to="/login"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Go to Login
              </Link>
            ) : (
              <button
                onClick={fetchProgress}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Try Again
              </button>
            )}
            <Link
              to="/dashboard/projects"
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded"
            >
              Back to Projects
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-gray-600">
        No progress data available.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <Link 
          to={`/dashboard/projects/${projectId}`} 
          className="inline-flex items-center gap-2 text-blue-600 hover:underline"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Project
        </Link>
        <button 
          onClick={fetchProgress}
          disabled={loading}
          className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          title="Refresh data"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <h1 className="text-2xl font-bold text-gray-800">
        Project Progress – {data.project?.title || 'Untitled Project'}
      </h1>

      <div className="flex flex-wrap items-center gap-6">
        <div className="text-xl font-semibold">
          Completion: <span className={getProgressColor(completionPercentage)}>
            {completionPercentage}% {hasTasks && `(${data.tasks.completed}/${data.tasks.total} tasks)`}
          </span>
        </div>
        {daysRemaining !== null && (
          <div className="text-xl font-semibold">
            Deadline: <span className={daysRemaining < 30 ? "text-red-600" : "text-yellow-600"}>
              {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <h2 className="text-lg font-semibold mb-4">Task Status Overview</h2>
          <div className="h-[250px]">
            {hasTasks ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number) => [`${value} task${value !== 1 ? 's' : ''}`, 'Count']}
                    contentStyle={{ 
                      borderRadius: '0.5rem', 
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  />
                  <Bar dataKey="value" name="Tasks" barSize={30}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No tasks found for this project
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <h2 className="text-lg font-semibold mb-4">Task Distribution</h2>
          <div className="h-[280px] w-full">
            {hasTasks ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie 
                    data={chartData} 
                    dataKey="value" 
                    nameKey="name" 
                    cx="50%" 
                    cy="50%" 
                    outerRadius={90}
                    innerRadius={60}
                    paddingAngle={2}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number, name: string, props: any) => {
                      const percent = props.payload.percent * 100;
                      return [
                        `${value} task${value !== 1 ? 's' : ''} (${percent.toFixed(1)}%)`,
                        name
                      ];
                    }}
                    contentStyle={{ 
                      borderRadius: '0.5rem', 
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  />
                  <Legend 
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No tasks found for this project
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectProgress;